export type StatusRef = { code: string; displayName: string } | null | undefined;

export interface DisplayStatus {
  label: string;
  cssClass: string;
}

// Shipment codes that end the Build's run on a job (in the field, or the job/shipment was cancelled);
// these stay on the shipment axis.
const FIELD_END_SHIPMENT_CODES = ["DOWNHOLE", "LIH", "LOST", "IN_CUSTOMER_POSSESSION", "CANCELLED"];

// Underscores become separate words so new codes hit the same status.global.css word classes
// the legacy strings use (PARTIAL_RECEIVED -> "partial received").
export function displayStatus(current: StatusRef, legacy?: string | null): DisplayStatus | null {
  if (current) {
    return { label: current.displayName, cssClass: current.code.toLowerCase().replaceAll("_", " ") };
  }
  if (legacy) {
    return { label: legacy, cssClass: legacy.toLowerCase() };
  }
  return null;
}

// Lifecycle until the Build ships, then the shipment axis. RECEIVED holds until the shop moves the
// lifecycle past READY (DISASSEMBLING/DISASSEMBLED), which hands it back to the lifecycle axis.
export function buildShownStatus(build: { currentStatus?: StatusRef; currentShipmentStatus?: StatusRef }): StatusRef {
  const lifecycle = build.currentStatus;
  const shipment = build.currentShipmentStatus;
  const code = shipment?.code;
  const showShipment =
    code === "SHIPPED" ||
    (!!code && FIELD_END_SHIPMENT_CODES.includes(code)) ||
    (code === "RECEIVED" && (!lifecycle || lifecycle.code === "READY"));
  return showShipment ? shipment : lifecycle;
}

export function buildDisplayStatus(
  build: { currentStatus?: StatusRef; currentShipmentStatus?: StatusRef },
  legacy?: string | null
): DisplayStatus | null {
  return displayStatus(buildShownStatus(build), legacy);
}

// Inventory axis, unless the Component is ISSUED onto a Build — then the allocation axis.
export function componentShownStatus(component: {
  currentInventoryStatus?: StatusRef;
  currentAllocationStatus?: StatusRef;
}): StatusRef {
  const inventory = component.currentInventoryStatus;
  const allocation = component.currentAllocationStatus;
  const showAllocation = inventory?.code === "ISSUED" && !!allocation && allocation.code !== "UNASSIGNED";
  return showAllocation ? allocation : inventory;
}

export function componentDisplayStatus(
  component: { currentInventoryStatus?: StatusRef; currentAllocationStatus?: StatusRef },
  legacy?: string | null
): DisplayStatus | null {
  return displayStatus(componentShownStatus(component), legacy);
}

const PRE_SHIP_JOB_CODES = ["PLANNING", "FULFILLING", "READY"];
const PRE_SHIP_LEGACY_JOB_STATUSES = ["PLANNING", "QUEUED", "BUILDING", "FULFILLING", "FULLFILLING", "RTS"];

// A Job that hasn't shipped yet, so Builds can still be added: its new status when it has one,
// otherwise the legacy statusString.
export function isPreShipJob(currentStatus: { code: string } | null | undefined, legacy?: string | null): boolean {
  if (currentStatus) return PRE_SHIP_JOB_CODES.includes(currentStatus.code);
  return PRE_SHIP_LEGACY_JOB_STATUSES.includes((legacy ?? "").trim().toUpperCase());
}

// Map-icon colors per shipment status code. Mirrors status.global.css's hues (which Mapbox can't read),
// darkened so each clears 4:1 against the icons' white halo — the CSS chip fills (palegoldenrod, salmon,
// tan...) are too pale to read as a map marker.
const SHIPMENT_STATUS_MAP_COLORS: Record<string, string> = {
  PREPARING: "#0077b6",
  READY: "#008000",
  SHIPPED: "#2e7d32",
  IN_TRANSIT: "#8d7b00",
  RETURNING: "#8d7b00",
  ARRIVED: "#c0503a",
  COMPLETE: "#8b6b3e",
  CANCELLED: "#c62828",
  VOID: "#c62828",
};
const SHIPMENT_STATUS_MAP_FALLBACK = "#546e7a";

/** Every distinct color shipmentStatusMapColor can return, for pre-rendering per-color map icons. */
export const SHIPMENT_STATUS_MAP_PALETTE: readonly string[] = [
  ...new Set([...Object.values(SHIPMENT_STATUS_MAP_COLORS), SHIPMENT_STATUS_MAP_FALLBACK]),
];

export function shipmentStatusMapColor(code: string | null | undefined): string {
  return (code && SHIPMENT_STATUS_MAP_COLORS[code]) || SHIPMENT_STATUS_MAP_FALLBACK;
}
